import React, { Component } from 'react';
import { connect } from 'react-redux';
import Dashboard from "../Dashboard";
import ModuleHeader from "../elements/ModuleHeader";
import SectionHeader from "../elements/SectionHeader";
import server from "../../../api/server";
import Loader from "../../Loader";
import { errorToast, successToast, copiedToast } from "../commandbuilder/toast";
import moment from "moment";
import 'moment-timezone';
import history from '../../../history';

export class PremiumVoting extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            votingBots: [
                {
                    id: "1331245032568524861",
                    name: "GhostBuddy",
                    image: "https://cdn.discordapp.com/avatars/1331245032568524861/a_5759d91dea4421eeed8447ae2c4192f2.gif",
                    voteLink: "https://top.gg/bot/1331245032568524861/vote",
                    lastVoted: null
                }
            ],
            votingHistory: [],
            voteCount: 0,
            totalVotesNeeded: 10,
            upgradeCodes: []
        };
    }

    componentDidMount() {
        if (this.props.bot?.id) {
            this.fetchVotingData();
            this.initializeVotingBots();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.bot?.id !== this.props.bot?.id) {
            this.fetchVotingData();
            this.initializeVotingBots();
        }
    }

    fetchVotingData = async () => {
        try {
            this.setState({ loaded: false });

            const response = await server.get('/topgg/status');

            if (response.data.success) {
                // Process voting history to add bot names and images
                const votingHistory = response.data.votingHistory || [];
                const votingBots = response.data.votingBots.length > 0
                    ? response.data.votingBots
                    : this.state.votingBots;

                // Map bot IDs to names and images in voting history and find last vote dates
                const processedHistory = votingHistory.map(vote => {
                    // Find matching bot in votingBots array
                    const matchingBot = votingBots.find(bot => bot.id === vote.botId);

                    return {
                        ...vote,
                        botName: matchingBot ? matchingBot.name : vote.botName || vote.botId,
                        botImage: matchingBot ? matchingBot.image : vote.botImage
                    };
                });

                // Find the last vote date for each bot from voting history
                const processedBots = votingBots.map(bot => {
                    const botVotes = processedHistory.filter(vote => vote.botId === bot.id);
                    const lastVote = botVotes.length > 0
                        ? botVotes.reduce((latest, vote) => {
                            const voteDate = new Date(vote.timestamp);
                            return latest > voteDate ? latest : voteDate;
                        }, new Date(0))
                        : null;

                    return {
                        ...bot,
                        lastVoted: lastVote && lastVote.getTime() > 0 ? lastVote : null
                    };
                });

                this.setState({
                    loaded: true,
                    voteCount: response.data.voteCount || 0,
                    votingHistory: processedHistory,
                    votingBots: processedBots,
                    upgradeCodes: response.data.upgradeCodes || []
                });
            } else {
                errorToast("Error", response.data.error || "Failed to fetch voting data");
                this.setState({ loaded: true });
            }
        } catch (error) {
            errorToast("Error", "Failed to fetch voting data");
            this.setState({ loaded: true });
        }
    };

    initializeVotingBots = async () => {
        try {
            // Initialize the default voting bots on the server
            await server.post('/topgg/init-bots', {
                bots: this.state.votingBots
            });
        } catch (error) {
            console.error("Failed to initialize voting bots:", error);
        }
    };

    handleVoteClick = (bot) => {
        // Open the voting link in a new tab
        window.open(bot.voteLink, "_blank");
    };

    handleRedeemReward = async () => {
        try {
            const response = await server.post('/topgg/redeem');

            if (response.data.success) {
                successToast("Success", `You have redeemed a premium code for 5 days! Code: ${response.data.code}`);

                // Refresh voting data to update the UI
                this.fetchVotingData();
            } else {
                errorToast("Error", response.data.error || "Failed to redeem reward");
            }
        } catch (error) {
            errorToast("Error", "Failed to redeem reward");
        }
    };

    navigateToSettings = () => {
        history.push("/dashboard/settings");
    };

    copyCodeToClipboard = (code) => {
        navigator.clipboard.writeText(code).then(() => {
            copiedToast("Code copied to clipboard!");
        });
    };

    renderTimeAgo = (timestamp) => {
        if (!timestamp) return "Never";
        return moment(timestamp).fromNow();
    };

    isWeekendBonusActive = () => {

        const now = moment().tz('UTC');
        const dayOfWeek = now.day(); // 0 is Sunday, 6 is Saturday
        return dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6;
    };

    renderProgressBar = () => {
        const { voteCount, totalVotesNeeded } = this.state;
        const progressPercentage = Math.min((voteCount / totalVotesNeeded) * 100, 100);
        const canRedeem = voteCount >= totalVotesNeeded;
        const isWeekend = this.isWeekendBonusActive();

        return (
            <div className={`${isWeekend ? 'bg-[#1a0f2a]' : 'bg-gray'} text-white p-6 rounded-lg mb-5 relative overflow-hidden`}>
                {isWeekend && (
                    <>
                        <div className="absolute -bottom-32 right-0 w-2/3 h-[150%] bg-gradient-to-tl from-[#ff00ff]/30 via-[#9900ff]/20 to-transparent blur-3xl animate-pulse" />
                        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-[#ff00ff]/25 via-[#9900ff]/15 to-transparent blur-2xl animate-pulse" />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#ff00ff]/[0.05] to-[#9900ff]/[0.08]" />
                    </>
                )}

                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                        <div>
                            <div className="flex items-center gap-4">
                                <h3 className="text-xl font-bold">Vote Progress</h3>
                                {isWeekend && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-[#ff00ff] to-[#9900ff] text-white shadow-lg shadow-purple-500/30 animate-pulse">
                                        2x WEEKEND BONUS!
                                    </span>
                                )}
                            </div>
                            <p className={`${isWeekend ? 'text-[#ff00ff]' : 'text-gray-400'}`}>
                                For every 10 votes, you can redeem 5 days of premium!
                                {isWeekend && " Each vote counts as 2 during the weekend!"}
                            </p>
                        </div>

                        {canRedeem && (
                            <div className="mt-3 md:mt-0">
                                <button
                                    className={`btn ${isWeekend ? '!bg-gradient-to-r from-[#ff00ff] to-[#9900ff]' : '!bg-blue'} !text-white px-6 py-2 rounded-md`}
                                    onClick={this.handleRedeemReward}
                                >
                                    Redeem Premium Code (5 Days)
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="w-full bg-gray-800 rounded-full h-8 mb-2 overflow-hidden">
                        <div
                            className={`${isWeekend ? '!bg-gradient-to-r from-[#ff00ff] to-[#9900ff]' : '!bg-blue'} h-8 rounded-full transition-all duration-500 ease-in-out flex items-center justify-center relative overflow-hidden group`}
                            style={{ width: progressPercentage > 0 ? `${progressPercentage}%` : '0%' }}
                        >
                            {/* Shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent 
                                translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

                            <span className="relative z-10 text-xs font-semibold text-white">
                                {voteCount}/{totalVotesNeeded}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-between text-xs text-gray-400 mt-2">
                        <span>0 votes</span>
                        <span>{totalVotesNeeded} votes = 5 days premium</span>
                    </div>
                </div>
            </div>
        );
    };

    renderWeekendHeader = () => {
        if (!this.isWeekendBonusActive()) return null;

        return (
            <div className="bg-gradient-to-r from-[#1a0f2a] to-[#2a1538] p-6 rounded-lg mb-5 relative overflow-hidden">
                {/* Glow effects */}
                <div className="absolute -bottom-32 right-0 w-2/3 h-[150%] bg-gradient-to-tl from-[#ff00ff]/30 via-[#9900ff]/20 to-transparent blur-3xl animate-pulse" />
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-[#ff00ff]/25 via-[#9900ff]/15 to-transparent blur-2xl animate-pulse" />

                {/* Weekend Multiplier Badge */}
                <div className="absolute -right-12 -top-12 w-48 h-48 rotate-12">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#ff00ff] to-[#9900ff] rounded-full opacity-20 animate-pulse"></div>
                    <div className="absolute inset-2 bg-gradient-to-br from-[#ff00ff] to-[#9900ff] rounded-full opacity-20 animate-pulse delay-100"></div>
                    <div className="absolute inset-4 bg-gradient-to-br from-[#ff00ff] to-[#9900ff] rounded-full opacity-20 animate-pulse delay-200"></div>
                </div>

                <div className="relative z-10 flex items-start gap-6">
                    <div className="flex-shrink-0 bg-[#ff00ff]/10 p-3 rounded-lg">
                        <svg className="h-8 w-8 text-[#ff00ff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div>
                        <div className="flex items-center gap-4">
                            <h2 className="text-2xl font-bold text-white">Weekend Voting Bonus Active!</h2>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-[#ff00ff] to-[#9900ff] text-white shadow-lg shadow-purple-500/30">
                                2x MULTIPLIER
                            </span>
                        </div>
                        <p className="text-[#ff00ff] mt-2">
                            Your votes count double during the weekend! Vote now to earn premium rewards faster.
                            Active until Sunday 23:59 UTC.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    renderVotingBots = () => {
        const { votingBots } = this.state;

        if (votingBots.length === 0) {
            return (
                <div className="col-span-full text-center text-white p-5">
                    <p>No voting bots available at this time. Please check back later.</p>
                </div>
            );
        }

        return votingBots.map(bot => (
            <div key={bot.id} className="relative text-white flex items-center justify-between rounded-lg bg-gray px-6 py-6">
                <div
                    className="flex items-center space-x-4 cursor-pointer"
                    onClick={() => window.open(`https://top.gg/bot/${bot.id}`, "_blank")}
                >
                    <div className="flex-shrink-0">
                        <img
                            className="h-16 w-16 rounded-full !border-2 !border-blue object-cover"
                            src={bot.image}
                            alt={`${bot.name} logo`}
                            onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = process.env.PUBLIC_URL + "/images/discord.jpg";
                            }}
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-bold text-white">{bot.name}</h3>
                        <div className="flex flex-col mt-1">
                            <p className="text-sm">
                                <span className="font-medium text-white">Last voted:</span>{" "}
                                <span className={`${bot.lastVoted ? 'text-green-400' : 'text-gray-400'}`}>
                                    {this.renderTimeAgo(bot.lastVoted)}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
                <div>
                    <button
                        className="btn !bg-blue !text-white px-6 py-2 rounded-md"
                        onClick={() => this.handleVoteClick(bot)}
                    >
                        Vote Now
                    </button>
                </div>
            </div>
        ));
    };

    renderVotingHistory = () => {
        const { votingHistory } = this.state;

        if (votingHistory.length === 0) {
            return (
                <div className="col-span-2 text-center text-white p-5 bg-gray rounded-lg">
                    <p className="py-4">No voting history found. Vote for our bots to earn free premium!</p>
                </div>
            );
        }

        return votingHistory.map((vote, index) => (
            <div key={index} className="relative text-white flex items-center justify-between rounded-lg bg-gray px-5 py-4">
                <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                        <img
                            className="h-12 w-12 rounded-full border-2 !border-blue object-cover"
                            src={vote.botImage || process.env.PUBLIC_URL + "/images/discord.jpg"}
                            alt={`${vote.botName || 'Bot'} logo`}
                            onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = process.env.PUBLIC_URL + "/images/discord.jpg";
                            }}
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white">
                            Voted for <span className="font-bold">{vote.botName || vote.botId}</span>
                        </p>
                        <p className="text-xs text-gray-400">
                            {moment(vote.timestamp).format('MMMM Do YYYY, h:mm:ss a')}
                        </p>
                    </div>
                </div>
                <div className="text-right bg-gray-800 px-3 py-1 rounded-full">
                    <p className="text-sm font-medium text-green-400">+{vote.voteValue || 1} vote</p>
                </div>
            </div>
        ));
    };

    renderUpgradeCodes = () => {
        const { upgradeCodes } = this.state;

        if (upgradeCodes.length === 0) {
            return (
                <div className="col-span-2 text-center text-white p-5 bg-gray rounded-lg">
                    <p className="py-4">No premium codes available. Vote for our bots to earn free premium!</p>
                </div>
            );
        }

        return (
            <>
                <div className="bg-gray text-white p-6 rounded-lg mb-5">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <p className="text-white">
                            You can redeem these premium codes in the <span className="font-bold">Settings</span> page
                        </p>
                        <button
                            className="btn !bg-blue !text-white px-6 py-2 rounded-md mt-3 md:mt-0"
                            onClick={this.navigateToSettings}
                        >
                            Go to Settings
                        </button>
                    </div>
                </div>

                {upgradeCodes.map((codeData, index) => (
                    <div
                        key={index}
                        className={`relative text-white flex items-center justify-between rounded-lg bg-gray px-5 py-4 mb-3 ${codeData.redeemed ? 'opacity-70' : ''}`}
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center">
                                <p className="text-sm font-medium text-white">
                                    Premium Code: <span className="font-mono bg-gray-800 px-2 py-1 rounded text-blue-300">{codeData.code}</span>
                                </p>
                                {codeData.redeemed && (
                                    <span className="ml-3 bg-gray-800 text-yellow-400 text-xs px-2 py-1 rounded">
                                        Redeemed
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                Created: {moment(codeData.createdAt).format('MMMM Do YYYY')} •
                                Duration: <span className="text-blue-300 font-semibold">{codeData.days} days</span>
                            </p>
                        </div>
                        <div>
                            <button
                                className={`btn !text-white px-6 py-2 text-sm rounded-md ${codeData.redeemed ? '!bg-gray-600' : '!bg-blue'}`}
                                onClick={() => this.copyCodeToClipboard(codeData.code)}
                                disabled={codeData.redeemed}
                            >
                                {codeData.redeemed ? 'Already Used' : 'Copy Code'}
                            </button>
                        </div>
                    </div>
                ))}
            </>
        );
    };

    render() {
        if (!this.state.loaded) {
            return (
                <Dashboard>
                    <ModuleHeader
                        showEnabled={false}
                        description="Vote for our bots on top.gg and earn free premium"
                        title="Premium Voting"
                        icon="vote.png"
                    />
                    <section>
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-32 w-32 !border-t-2 !border-b-2 !border-blue"></div>
                        </div>
                    </section>
                </Dashboard>
            );
        }

        const isWeekend = this.isWeekendBonusActive();

        return (
            <Dashboard>
                <ModuleHeader
                    showEnabled={false}
                    description={isWeekend ? "Weekend bonus active! Vote now for 2x rewards!" : "Vote for our bots on top.gg and earn free premium"}
                    title="Premium Voting"
                    icon="topggvote.png"
                />

                <div className="mt-8">
                    {this.renderWeekendHeader()}
                </div>

                <section className="mb-8">
                    <SectionHeader title="Vote Progress" pretitle="Premium" />
                    <div className="mt-4">
                        {this.renderProgressBar()}
                    </div>
                </section>

                <section className="mb-8">
                    <SectionHeader title="Available Voting Links" pretitle="Premium" />
                    <div className={`${isWeekend ? 'bg-[#1a0f2a]' : 'bg-gray'} text-white p-6 rounded-lg mb-5 mt-4 relative overflow-hidden`}>
                        {isWeekend && (
                            <>
                                <div className="absolute -bottom-32 right-0 w-2/3 h-[150%] bg-gradient-to-tl from-[#ff00ff]/30 via-[#9900ff]/20 to-transparent blur-3xl" />
                                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-[#ff00ff]/25 via-[#9900ff]/15 to-transparent blur-2xl" />
                            </>
                        )}
                        <div className="relative z-10">
                            <div className="text-center mb-5">
                                <h3 className="text-xl font-bold">Vote for Premium</h3>
                                <p className="text-gray-400">Vote for our bots on top.gg and earn free premium time!</p>
                            </div>

                            <div className="grid grid-cols-1 gap-4">
                                {this.renderVotingBots()}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mb-8">
                    <SectionHeader title="Premium Codes" pretitle="Premium" />
                    <div className="mt-4">
                        {this.renderUpgradeCodes()}
                    </div>
                </section>

                <section className="mb-8">
                    <SectionHeader title="Voting History" pretitle="Premium" />
                    <div className="grid grid-cols-1 mt-4 gap-4">
                        {this.renderVotingHistory()}
                    </div>
                </section>
            </Dashboard>
        );
    }
}

const mapStateToProps = (state) => ({
    bot: state.data.bot,
    user: state.data.user
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(PremiumVoting);